import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { WalletBalanceHero } from "./components/wallet-balance-hero";
import { MerchantKPICards } from "./components/merchant-kpi-cards";
import { VolumeChart } from "./components/volume-chart";
import { QuickActionsEnhanced } from "./components/quick-actions-enhanced";
import {
  fetchMerchantDashboardData,
  fetchMerchantTodayStats,
  formatRecentActivityForChart,
  calculateSuccessRate,
  type MerchantDashboardData,
} from "@/services/api/services/merchant-dashboard";
import { fetchTransactions } from "@/services/api/services/transactions";
import { Button } from "@/ui/components/primitives/button";
import { StatusBadge } from "@/modules/shared/components/status-badge";
import { TransactionTypeBadge } from "@/modules/shared/components/transaction-type-badge";
import { TransactionDetailModal } from "@/modules/transactions/components/transaction-detail-modal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/components/primitives/table";
import { Skeleton } from "@/ui/components/primitives/skeleton";
import { Badge } from "@/ui/components/primitives/badge";
import { RefreshCw } from "lucide-react";
import { formatDateIST } from "@/utils/date-formatter";

export function MerchantDashboard() {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<MerchantDashboardData | null>(null);
  const [todayStats, setTodayStats] = useState<any>(null);
  const [recentTransactions, setRecentTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [transactionsError, setTransactionsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const loadData = async () => {
    try {
      setError(null);

      // Fetch dashboard and stats first
      const [data, stats] = await Promise.all([
        fetchMerchantDashboardData(),
        fetchMerchantTodayStats(),
      ]);
      setDashboardData(data);
      setTodayStats(stats);

      // Fetch transactions separately to avoid failing the whole dashboard
      try {
        const transactions = await fetchRecentTransactions();
        setRecentTransactions(transactions);
        setTransactionsError(false);
      } catch (txnError) {
        console.error('Failed to fetch transactions:', txnError);
        // Still continue with dashboard even if transactions fail
        setRecentTransactions([]);
        setTransactionsError(true);
      }
    } catch (err: any) {
      console.error('Dashboard load error:', err);
      // Don't set error immediately - try to use cached/default data
      setError(null);
      // Set default data if API fails
      if (!dashboardData) {
        setDashboardData({
          merchant: {
            id: '',
            name: 'Merchant',
            status: 'ACTIVE',
            kycVerified: false,
          },
          wallet: {
            availableBalance: 0,
            reservedBalance: 0,
            totalBalance: 0,
            currency: 'INR',
          },
          feeConfiguration: {
            payin: { percentage: 0, fixed: 0 },
            payout: { percentage: 0, fixed: 0 },
          },
          statistics: {
            transactionStats: {
              totalTransactions: 0,
              successfulTransactions: 0,
              failedTransactions: 0,
              totalVolume: 0,
              averageTransactionValue: 0,
            },
            recentActivity: [],
            monthlyTrends: [],
          },
        });
      }
    } finally {
      setLoading(false);
      setTransactionsLoading(false);
      setRefreshing(false);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const response = await fetchTransactions({
        page: 1,
        limit: 10,
        status: 'SUCCESS',
        type: 'PAYIN,PAYOUT'
      });
      return response.transactions || [];
    } catch (error) {
      console.error('Error fetching recent transactions:', error);
      throw error; // Re-throw so we can handle it separately
    }
  };

  const retryTransactions = async () => {
    setTransactionsLoading(true);
    setTransactionsError(false);
    try {
      const transactions = await fetchRecentTransactions();
      setRecentTransactions(transactions);
    } catch (error) {
      console.error('Retry failed:', error);
      setRecentTransactions([]);
      setTransactionsError(true);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleTransactionClick = (transaction: any) => {
    // Pass the raw transaction directly (same as transaction book page)
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTransaction(null);
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
  };

  if (loading) {
    return (
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-80" />
        </div>
        <Skeleton className="h-40 w-full" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
        <Skeleton className="h-80 w-full" />
      </div>
    );
  }

  // Always render the dashboard with default data if needed
  const data = dashboardData || {
    merchant: { name: 'Merchant', status: 'ACTIVE', kycVerified: false },
    wallet: { availableBalance: 0, reservedBalance: 0, totalBalance: 0 },
    statistics: { transactionStats: { totalTransactions: 0, averageTransactionValue: 0 } },
    feeConfiguration: {
      payin: { percentage: 0, fixed: 0 },
      payout: { percentage: 0, fixed: 0 }
    }
  };

  const volumeChartData = formatRecentActivityForChart(
    data?.statistics?.recentActivity || []
  );

  // Format monthly trends data for chart
  const monthlyChartData = (data?.statistics?.monthlyTrends || []).map(trend => ({
    date: new Date(trend.month + "-01").toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    }),
    payin: trend.volume,
    payout: 0 // API doesn't provide monthly payout breakdown
  }));

  const successRate = calculateSuccessRate(data?.statistics?.transactionStats || {
    totalTransactions: 0,
    successfulTransactions: 0
  });

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {data?.merchant?.name || 'Merchant'}! Here's your payment gateway overview
          </p>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={(data?.merchant?.status || 'ACTIVE').toLowerCase()} />
            {!(data?.merchant?.kycVerified) && (
              <Badge variant="outline" className="text-warning border-warning">
                KYC Pending
              </Badge>
            )}
          </div>
        </div>
        <Button
          onClick={handleRefresh}
          variant="outline"
          size="sm"
          disabled={refreshing}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Wallet Balance Hero Card */}
      <WalletBalanceHero wallet={data?.wallet} />

      {/* KPI Metrics */}
      <MerchantKPICards
        kpiData={todayStats || {
          todayPayin: { amount: 0, count: 0, change: 0 },
          todayPayout: { amount: 0, count: 0, change: 0 },
          successRate: { percentage: 0, dailyChange: 0 },
        }}
        totalVolume={todayStats?.totalVolume || 0}
      />

      {/* Volume Chart */}
      <VolumeChart weekData={volumeChartData} monthData={monthlyChartData} />

      {/* Recent Transactions & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className="lg:col-span-2">
          <div className="border rounded-lg p-3 sm:p-6 bg-card">
            <div className="flex items-start justify-between gap-3 mb-4 sm:mb-6">
              <div>
                <h3 className="text-base sm:text-lg font-semibold">Recent Transactions</h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Latest Success Collection activities
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/transactions")}
              >
                View All
              </Button>
            </div>

            {transactionsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : transactionsError ? (
              <div className="text-center py-12 text-muted-foreground">
                <div className="space-y-4">
                  <p className="text-sm">Failed to load transactions</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={retryTransactions}
                    className="mx-auto"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry
                  </Button>
                </div>
              </div>
            ) : recentTransactions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <p>No recent transactions</p>
              </div>
            ) : (
              <div className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 hidden lg:table-cell">Type</TableHead>
                      <TableHead className="min-w-[120px] sm:min-w-[140px] max-w-[180px]">Reference</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Amount</TableHead>
                      <TableHead className="text-right">Fee</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recentTransactions.map((transaction: any) => (
                      <TableRow
                        key={transaction.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => handleTransactionClick(transaction)}
                      >
                        <TableCell className="hidden lg:table-cell">
                          <TransactionTypeBadge type={transaction.type} />
                        </TableCell>
                        <TableCell className="font-mono text-xs sm:text-sm max-w-[150px] sm:max-w-none truncate">
                          <span className="block sm:inline truncate">
                            {transaction.reference || transaction.referenceId || transaction.id}
                          </span>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={transaction.status} />
                        </TableCell>
                        <TableCell className="text-center font-semibold text-xs sm:text-sm whitespace-nowrap">
                          ₹{transaction.amount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-xs sm:text-sm text-muted-foreground">
                          ₹{(transaction.feeAmount || transaction.fee || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <QuickActionsEnhanced
          transactionStats={data?.statistics?.transactionStats}
          merchantName={data?.merchant?.name}
          successRate={successRate}
          feeConfiguration={data?.feeConfiguration}
        />
      </div>

      {/* Transaction Detail Modal */}
      {isModalOpen && selectedTransaction && (
        <TransactionDetailModal
          open={isModalOpen}
          onOpenChange={closeModal}
          txn={selectedTransaction}
        />
      )}
    </div>
  );
}